import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Card, Text } from 'react-native-paper';

import { PersonIcon } from '@/assets';
import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { GeoAreaRoute } from '@/features/geo-area/navigation/geoAreaRoute';
import { OrganizationLogo } from '@/features/media/components/avatar/OrganizationLogo';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { usePostCard } from '@/features/posts/components/feed/PostCardContext';
import { usePostCardHeaderContent } from '@/features/posts/components/feed/usePostCardHeaderContent';
import { PostTypeButton } from '@/features/posts/components/PostTypeButton';
import { RestrictedLabel } from '@/features/posts/components/RestrictedLabel';
import { VerifiedName } from '@/features/posts/components/VerifiedName';
import { useGetDorfHelferTypeName } from '@/features/posts/hooks/useGetDorfHelferTypeName';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { PastTime } from '@/shared/components/PastTime';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const AVATAR_SIZE = 48;

const headerStyles = StyleSheet.create({
  title: {
    flexDirection: 'row',
    paddingVertical: 16,
    justifyContent: 'flex-start',
    gap: 8,
    alignItems: 'center',
    overflow: 'hidden'
  },
  titleContent: {
    flex: 1
  },
  subtitle: {
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  }
});

export const PostCardHeader: FC<{
  hidePostType?: boolean;
  isGroupFeed?: boolean;
}> = memo(({ hidePostType = false, isGroupFeed = false }) => {
  const {
    groupId,
    organizationId,
    katwarnEventCodeText,
    postType,
    dorfHelferType,
    categories,
    isBlocked,
    images,
    creatorOrSiteName,
    creatorOrSitePicture,
    created,
    hiddenForOtherHomeAreas,
    geoAreaIds
  } = usePostCard();
  const { data: group } = useGetGroup(groupId);
  const { data: organization } = useGetOrganizationById(organizationId);
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POSTS);
  const { isEmergencyOrganization } = useAppState();
  const isEmergency = isEmergencyOrganization(organizationId);
  const isCivilProtection = !isEmptyOrNull(katwarnEventCodeText);

  const isOrganizationPost = !isEmptyOrNull(organizationId);
  const displayName = isOrganizationPost
    ? (organization?.name ?? creatorOrSiteName)
    : creatorOrSiteName;

  const navigation = useAppNavigation();
  const handleOrganizationPress = useCallback(() => {
    if (!organizationId) return;
    navigation.navigate(GeoAreaRoute.OrganizationProfile, {
      id: organizationId
    });
  }, [navigation, organizationId]);

  const localType: ClientPostTypeEnum | PostFeedType = useMemo(() => {
    if (isEmergency) return PostFeedType.EMERGENCY;
    if (isCivilProtection) return PostFeedType.CIVIL_PROTECTION;
    return postType ?? ClientPostTypeEnum.Gossip;
  }, [isCivilProtection, isEmergency, postType]);

  const dorfHelferTypeName = useGetDorfHelferTypeName(dorfHelferType);

  const cardHeaderContent = usePostCardHeaderContent({
    postType: localType,
    category: dorfHelferTypeName ?? categories
  });

  const titleMarginTop = useMemo(() => {
    if (isBlocked || images?.[0] || !(!isGroupFeed && cardHeaderContent))
      return 0;
    return 12;
  }, [cardHeaderContent, images, isBlocked, isGroupFeed]);

  // Note: Using Card.Title was leading to hight "limitations" and was causing issues on iOS: PostTypeButton was not working and was opening just the post
  return (
    <Card.Content style={[headerStyles.title, { marginTop: titleMarginTop }]}>
      {isOrganizationPost ? (
        <Pressable
          onPress={handleOrganizationPress}
          accessibilityRole="button"
          accessibilityLabel={organization?.name}
          accessibilityHint={t(
            'components.postCardHeader.accessibility.organizationHint',
            { name: organization?.name }
          )}
        >
          <OrganizationLogo
            mediaItem={organization?.logoImage}
            size={AVATAR_SIZE}
          />
        </Pressable>
      ) : (
        <ProfileAvatar
          viewSize={AVATAR_SIZE}
          iconFallback={PersonIcon}
          mediaItem={isBlocked ? undefined : creatorOrSitePicture}
          mediaSize={MediaItemSize.SMALL}
          backgroundColor={isCivilProtection ? theme.colors.error : undefined}
        />
      )}
      <View style={headerStyles.titleContent}>
        <VerifiedName
          name={displayName}
          verified={isOrganizationPost}
          onPress={isOrganizationPost ? handleOrganizationPress : undefined}
        />
        <View style={headerStyles.subtitle}>
          {postType !== ClientPostTypeEnum.Happening && (
            <Text variant="bodyMedium">
              <PastTime time={created} withoutSuffix />
            </Text>
          )}
          {!group && (
            <RestrictedLabel
              hiddenForOtherHomeAreas={hiddenForOtherHomeAreas}
              geoAreaIds={geoAreaIds}
              hideDot={postType === ClientPostTypeEnum.Happening}
              asInline
            />
          )}
          {!hidePostType && (postType || group) && (
            <PostTypeButton postType={localType} group={group} asChip />
          )}
        </View>
      </View>
    </Card.Content>
  );
});
PostCardHeader.displayName = 'PostCardHeader';
